import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Trash2, ShieldOff, HeartHandshake, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const err = (e) => { const d = e?.response?.data?.detail; return Array.isArray(d) ? d.map((x) => x.msg).join(' ') : d || 'Something went wrong'; };
const fmt = (iso) => (iso ? new Date(String(iso).match(/Z|[+-]\d\d:\d\d$/) ? iso : `${iso}Z`).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '');

export const BlockedMembers = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const load = () => api.get('/community/blocks').then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const unblock = async (r) => { try { await api.delete(`/community/block/${r.blocked_id}`); toast({ title: `${r.blocked_name || 'Member'} unblocked` }); load(); } catch (e) { toast({ title: 'Failed', description: err(e) }); } };
  return (
    <section className="mt-10 bg-white/70 rounded-3xl border border-rose/40 p-6" data-testid="blocked-members">
      <h2 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><ShieldOff className="w-5 h-5 text-rose-italic shrink-0" /> Blocked members</h2>
      <p className="text-sm text-mahogany/70 mt-1">You won't see each other's posts, comments or live-chat messages, and they can't comment on your posts.</p>
      {rows.length === 0 ? <p className="text-sm text-mahogany/60 mt-3" data-testid="blocked-empty">You haven't blocked anyone.</p> : (
        <div className="mt-3 divide-y divide-rose/25">
          {rows.map((r) => (
            <div key={r.blocked_id} className="flex items-center justify-between py-2 text-sm" data-testid={`blocked-${r.blocked_id}`}>
              <span className="text-burgundy">{r.blocked_name || 'Member'}</span>
              <button onClick={() => unblock(r)} className="pill-btn-outline !py-1 text-xs" data-testid={`unblock-${r.blocked_id}`}>Unblock</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export const MonthlyGiftLink = () => (
  <section className="mt-10 bg-white/70 rounded-3xl border border-rose/40 p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="monthly-gift-card">
    <div><h2 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><HeartHandshake className="w-5 h-5 text-rose-italic shrink-0" /> Monthly giving</h2>
      <p className="text-sm text-mahogany/70 mt-1">Change the amount, update your card or cancel a monthly gift.</p></div>
    <Link to="/giving/manage" className="pill-btn-outline text-sm" data-testid="monthly-gift-manage">Manage monthly gift</Link>
  </section>
);

const DeleteModal = ({ onClose, onDone }) => {
  const { toast } = useToast();
  const [f, setF] = useState({ mode: 'scheduled', password: '', confirm: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post('/account/delete', { ...f, password: f.password || null }); onDone(r.data); }
    catch (e2) { toast({ title: 'Could not delete', description: err(e2) }); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-cream rounded-3xl p-6 max-w-md w-full space-y-3" data-testid="delete-account-modal">
        <h3 className="font-serif-display text-2xl text-burgundy flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" /> Delete my account</h3>
        <p className="text-sm text-burgundy/80">This removes your login, profile, community posts, comments and notifications. Records of past bookings and donations (with your name and contact) are kept for our accounting, tax and audit records.</p>
        <label className="flex items-start gap-2 text-sm text-burgundy"><input type="radio" checked={f.mode === 'scheduled'} onChange={() => setF({ ...f, mode: 'scheduled' })} data-testid="delete-mode-scheduled" className="mt-1" /> <span><strong>Delete in 30 days</strong> — sign in any time before then to keep it</span></label>
        <label className="flex items-start gap-2 text-sm text-burgundy"><input type="radio" checked={f.mode === 'now'} onChange={() => setF({ ...f, mode: 'now' })} data-testid="delete-mode-now" className="mt-1" /> <span><strong>Delete right now</strong> — this can't be undone</span></label>
        <input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Your password (not needed for Google sign-in)" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm" data-testid="delete-password" />
        <input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Why are you leaving? (optional)" className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm" data-testid="delete-reason" />
        <input value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} placeholder='Type DELETE to confirm' className="w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-sm" data-testid="delete-confirm" />
        <div className="flex gap-2 justify-end pt-1">
          <button type="button" onClick={onClose} className="pill-btn-outline text-sm">Cancel</button>
          <button disabled={busy || f.confirm.trim().toUpperCase() !== 'DELETE'} className="pill-btn text-sm !bg-red-700" data-testid="delete-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} {f.mode === 'now' ? 'Delete now' : 'Schedule deletion'}</button>
        </div>
      </form>
    </div>
  );
};

export const AccountDeletion = () => {
  const { toast } = useToast();
  const { logout } = useAuth();
  const [scheduled, setScheduled] = useState(undefined);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get('/account/deletion').then((r) => setScheduled(r.data.scheduled_at)).catch(() => setScheduled(null)); }, []);
  const keep = async () => { try { await api.post('/account/delete/cancel'); setScheduled(null); toast({ title: 'Your account will not be deleted' }); } catch (e) { toast({ title: 'Failed', description: err(e) }); } };
  const done = async (d) => {
    setOpen(false);
    if (d.deleted) { toast({ title: 'Your account has been deleted' }); try { await logout(); } catch { /* already gone */ } window.location.href = '/'; }
    else { setScheduled(d.scheduled_at); toast({ title: `Scheduled for deletion on ${fmt(d.scheduled_at)}` }); }
  };
  if (scheduled === undefined) return null;
  return (
    <section className="mt-10 rounded-3xl border border-red-200 bg-red-50/60 p-6" data-testid="account-deletion">
      <h2 className="font-serif-display text-2xl text-burgundy">Delete account</h2>
      {scheduled ? (
        <div className="mt-2 flex items-center justify-between gap-3 flex-wrap" data-testid="deletion-scheduled">
          <p className="text-sm text-red-800">Your account is scheduled to be deleted on <strong>{fmt(scheduled)}</strong>.</p>
          <button onClick={keep} className="pill-btn text-sm" data-testid="deletion-keep">Keep my account</button>
        </div>
      ) : (
        <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-mahogany/75">Permanently remove your account. You can choose a 30-day grace period.</p>
          <button onClick={() => setOpen(true)} className="pill-btn-outline text-sm !border-red-300 !text-red-700" data-testid="delete-account-open"><Trash2 className="w-4 h-4" /> Delete my account</button>
        </div>
      )}
      {open && <DeleteModal onClose={() => setOpen(false)} onDone={done} />}
    </section>
  );
};
